// Client-callable server functions for the referral program.
// NOTE: server-only imports (`client.server`, `referrals.server`) are loaded
// inside each .handler() body so they cannot leak into the client bundle.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  isValidReferralCode,
  REFERRAL_CAP_PER_YEAR,
} from "./referrals-shared";

// ─── getMyReferralCode ─────────────────────────────────────────────────────
export const getMyReferralCode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { userId } = context;
    const { data } = await (supabaseAdmin as any)
      .from("referral_codes")
      .select("code")
      .eq("user_id", userId)
      .maybeSingle();
    if (data?.code) return { code: data.code as string };

    // Safety net: if trigger didn't fire, issue one now.
    const { data: gen } = await (supabaseAdmin as any).rpc("generate_referral_code");
    const newCode: string = gen as string;
    await (supabaseAdmin as any)
      .from("referral_codes")
      .insert({ code: newCode, user_id: userId });
    return { code: newCode };
  });

// ─── getMyReferrals ────────────────────────────────────────────────────────
export const getMyReferrals = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { userId } = context;
    const { data } = await (supabaseAdmin as any)
      .from("referral_events")
      .select("id, status, created_at, credited_at, referred_user_id")
      .eq("referrer_user_id", userId)
      .order("created_at", { ascending: false });
    const rows: Array<{
      id: string;
      status: string;
      created_at: string;
      credited_at: string | null;
      referred_user_id: string;
    }> = data ?? [];

    // Pull masked emails for display
    const ids = rows.map((r) => r.referred_user_id);
    let emailMap: Record<string, string | null> = {};
    if (ids.length) {
      const { data: profs } = await supabaseAdmin
        .from("profiles")
        .select("id, email")
        .in("id", ids);
      for (const p of profs ?? []) {
        emailMap[p.id as string] = (p as any).email ?? null;
      }
    }

    const maskEmail = (e: string | null): string => {
      if (!e) return "friend";
      const [local, domain] = e.split("@");
      if (!domain) return "friend";
      const head = local.slice(0, 2);
      return `${head}***@${domain}`;
    };

    const creditedCount = rows.filter((r) => r.status === "credited").length;

    return {
      referrals: rows.map((r) => ({
        id: r.id,
        status: r.status,
        createdAt: r.created_at,
        creditedAt: r.credited_at,
        maskedEmail: maskEmail(emailMap[r.referred_user_id] ?? null),
      })),
      creditedCount,
    };
  });

// ─── claimReferralOnSignup ────────────────────────────────────────────────
// Called right after profile creation. Validates the code, enforces cap and
// self-referral, and inserts a pending referral_events row.
export const claimReferralOnSignup = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) =>
    z.object({ code: z.string().min(1).max(16) }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { countRecentCreditedReferrals } = await import("./referrals.server");
    const { safeError } = await import("./safe-error.server");
    const { userId } = context;
    const codeUp = data.code.toUpperCase();
    if (!isValidReferralCode(codeUp)) {
      return { ok: false as const, reason: "invalid_code" };
    }

    try {
      const { data: rc } = await (supabaseAdmin as any)
        .from("referral_codes")
        .select("user_id")
        .eq("code", codeUp)
        .maybeSingle();
      if (!rc?.user_id) return { ok: false as const, reason: "unknown_code" };

      const referrerUserId = rc.user_id as string;
      if (referrerUserId === userId) {
        return { ok: false as const, reason: "self_referral" };
      }

      const { data: existing } = await (supabaseAdmin as any)
        .from("referral_events")
        .select("id")
        .eq("referred_user_id", userId)
        .maybeSingle();
      if (existing) return { ok: false as const, reason: "already_referred" };

      const credited = await countRecentCreditedReferrals(referrerUserId);
      if (credited >= REFERRAL_CAP_PER_YEAR) {
        await (supabaseAdmin as any).from("referral_events").insert({
          referrer_user_id: referrerUserId,
          referred_user_id: userId,
          referral_code: codeUp,
          status: "rejected",
          rejection_reason: "cap_reached_at_claim_time",
        });
        return { ok: false as const, reason: "cap_reached" };
      }

      const { error: insErr } = await (supabaseAdmin as any)
        .from("referral_events")
        .insert({
          referrer_user_id: referrerUserId,
          referred_user_id: userId,
          referral_code: codeUp,
          status: "pending",
        });
      if (insErr) {
        safeError(insErr, { scope: "referrals.claim", context: { userId } });
        return { ok: false as const, reason: "insert_failed" };
      }
      return { ok: true as const };
    } catch (err) {
      safeError(err, { scope: "referrals.claim", context: { userId } });
      return { ok: false as const, reason: "insert_failed" };
    }
  });

